import { useEffect } from 'preact/hooks';
import { debounce } from 'lodash-es';

const hasFocus = el => el.contains(document.activeElement);

/**
 * Track whether the element contains the currently focused element
 * ("contains" meaning itself or a child element). It takes a callback which gets
 * invoked whenever the element gains or loses "contained" focus.
 */
export const useContainsFocus = ({ callback, ref, ignoreRef }) => {
  useEffect(() => {
    if (!ref.current?.addEventListener) {
      return; // not a dom element
    }
    // When focus changes between elements, there is a brief moment where nothing
    // is focused b/c the `blur` event on the previously focused element occurs
    // immediately before the `focus` event. That would translate to an extra,
    // unnecessary value emit that we can avoid using debounce.
    const handler = debounce(() => {
      setTimeout(() => callback(hasFocus(ref.current)));
    });

    // Would have preferred using listening for `focusin``focusout`, but Firefox
    // doesn't support them. Instead, listen for `focus`/`blur` (must use capture
    // phase because they don't bubble).
    // https://developer.mozilla.org/en-US/docs/Web/Events/focus#Event_delegation
    ref.current.addEventListener('focus', handler, true);
    ref.current.addEventListener('blur', handler, true);

    return () => {
      ref.current?.removeEventListener('focus', handler);
      ref.current?.removeEventListener('blur', handler);
    };
  }, [callback, ref, ignoreRef]);
};
